import React, { useEffect, useState, useRef } from 'react';
import {
  IconButton,
  InputAdornment,
  TableCell,
  TableRow,
  TextField,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import styles from '../styles';

type EnumerationsItemProps = {
  elements: any[];
  element: any;
  disabled?: boolean;
  index: number;
  removeEnum: (i: number) => void;
  editEnumerations: ({ index, field, value, regex, sizeLimit }) => void;
}

const EnumerationsItem = ({
  elements,
  element,
  disabled,
  index,
  removeEnum,
  editEnumerations,
}: EnumerationsItemProps) => {
  const errorRef = useRef('');
  const [errorText, setErrorText] = useState('');

  const [hasFocus, setHasFocus] = useState(false);
  const [touched, setTouched] = useState(false);
  const [changed, setChanged] = useState(false);

  const classes = styles();

  useEffect(() => {
   const newErrorText = getErrorText({ value: element.name, elements, disabled, index });
   errorRef.current = newErrorText;
  }, [element.name, elements, disabled, index, touched]);

  useEffect(() => {
    if (hasFocus || disabled || !touched || !changed) return;
    setErrorText(errorRef.current);
  }, [hasFocus, disabled, touched, changed]);

  const handleFocus = () => {
    setChanged(false);
    setHasFocus(true);
  }

  const handleBlur = () => {
    setHasFocus(false);
    setTouched(true);
  }

  return (
    <TableRow key={element.key}>
      <TableCell
        colSpan={2}
        padding="normal"
        className={classes.tableCell}
      >
        <TextField
          autoFocus={!element.name}
          disabled={disabled}
          size="small"
          variant="outlined"
          className={classes.tableField}
          value={element.name}
          InputProps={{
            endAdornment: !element.id ? (
              <DeleteEnum index={index} removeEnum={removeEnum} />
            ) : undefined,
          }}
          inputProps={{
            'data-testid': `${element.key}-enum-value`,
          }}
          error={!!errorText}
          helperText={errorText}
          fullWidth
          autoComplete="off"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={event => {
            setChanged(true);
            editEnumerations({
              index,
              field: 'name',
              value: event.target.value as string,
              regex: /[^A-Za-z0-9\-_\s?¿]/g,
              sizeLimit: 60,
            });
          }}
        />
      </TableCell>
    </TableRow>
  );
};

function getErrorText({ disabled, value, elements, index }) {
  if (disabled) return '';
  if (!value.trim()) return 'Item cannot be blank';
  const dupeIndex = elements.findIndex((e, i) => e.name === value && i !== index);
  if (dupeIndex === -1) return '';
  return `This is the same as item #${dupeIndex + 1}`;
}

function DeleteEnum({
    removeEnum,
    index,
  }: {
    removeEnum: (i: number) => void;
    index: number;
  }) {
    return (
      <InputAdornment position="end">
        <IconButton aria-label="close" onClick={() => removeEnum(index)}>
          <CloseIcon opacity="0.5" />
        </IconButton>
      </InputAdornment>
    );
  }

export default EnumerationsItem;